import { FC, useState } from 'react'
import cx from 'classnames'
import styles from './FilterableGallery.module.scss'
import { ImgBackground, Source } from 'components/basic/Img'
import { Select } from 'components/Select'
import { Heading } from 'components/basic/Heading'
import { Button } from 'components/WebEv/Buttons'
import { AnimateOnReveal } from 'components/AnimateOnScroll'

interface Item {
	name: string
	imageSrc: string
	imageAlt: string
	sport: string
	country: string
	category: string
	sources?: Source[]
}

interface Props {
	items: Item[]
	className?: string
}

interface FilterProps {
	setSportValue: (p: string) => void
	setCountryValue: (p: string) => void
	sports: string[]
	countries?: string[]
}

const GalleryItem = (props: Item) => {
	return (
		<ImgBackground
			sources={props.sources}
			lazy={false}
			src={props.imageSrc}
			alt={props.imageAlt || `${props.name} profile picture`}
			className={styles.background_image}
		>
			<strong>{props.name}</strong>
			<span>{props.sport}</span>
		</ImgBackground>
	)
}

const Filter = (props: FilterProps) => {
	const sportsItems = props.sports.map((it) => ({ value: it, text: it }))
	const countriesItems = props.countries?.map((it) => ({ value: it, text: it }))

	return (
		<div className={styles.filter}>
			<div className={styles.selects}>
				{countriesItems && countriesItems.length > 0 ? (
					<Select
						items={countriesItems}
						onSelection={(value) => props.setCountryValue(value?.toString() || '')}
						placeholder="Country"
						wrapperClassname={styles.select_wrapper}
						allowEmpty={false}
					/>
				) : null}
				<Select
					items={sportsItems}
					onSelection={(value) => props.setSportValue(value?.toString() || '')}
					placeholder="Sports"
					wrapperClassname={styles.select_wrapper}
					allowEmpty={false}
				/>
			</div>
		</div>
	)
}

const filterItems = (items: Item[], sport?: string, country?: string, limit?: number) => {
	let result = [...items]
	if (sport && sport !== 'All') {
		const _searchValue = sport.toLowerCase()
		result = result.filter((it) => it.category.toLowerCase().includes(_searchValue))
	}
	if (country && country !== 'All') {
		const _searchValue = country.toLowerCase()
		result = result.filter((it) => it.country && it.country.toLowerCase().includes(_searchValue))
	}
	return limit ? result.slice(0, limit) : result
}

export const FilterableGallery: FC<Props> = (props) => {
	const sports = ['All', ...new Set(props.items.map((it) => it.category))]
	const countries = ['All', ...new Set(props.items.map((it) => it.country))]

	// const [searchValue, setSearchValue] = useState('')
	const [selectedSport, setSelectedSport] = useState('')
	const [selectedCountry, setSelectedCountry] = useState('')
	const [limit, setLimit] = useState(12)

	return (
		<div className={cx('inner-paddings-x inner-paddings-y', styles.root, props.className)}>
			<div className={styles.container}>
				<Heading
					title="Powering the extraordinary"
					type="h2"
					titleClass={cx('h2', styles.heading)}
				/>
				<p>Join the ranks of elite athletes who have made the Pod an integral part of their training regimen.</p>
				<Filter
					sports={sports}
					countries={countries.filter((it) => it).length > 0 ? countries : undefined}
					setSportValue={setSelectedSport}
					setCountryValue={setSelectedCountry}
				/>
				<div className={`${styles.grid} ${limit ? styles.gradient : ''}`}>
					{filterItems(props.items, selectedSport, selectedCountry, limit).map((it, i) => (
						<AnimateOnReveal
							key={`${it.imageSrc}-${i}`}
							noMinHeight
						>
							<GalleryItem {...it} />
						</AnimateOnReveal>
					))}
				</div>
				{limit && (
					<div className="tac">
						<Button.Primary
							id={'see-more-athletes'}
							onClick={() => setLimit(null)}
							className={styles.see_more}
						>
							Show more athletes
						</Button.Primary>
					</div>
				)}
			</div>
		</div>
	)
}
